import React from "react";
import SearchableEmployeeSelect from "../../../../common/SearchableEmployeeSelect";
import SearchableGlAccountSelect from "../../../../common/SearchableGlAccountSelect";
import SearchableCodeSelect from "../../../../common/SearchableCodeSelect";
import ExpenseAch from "./ExpenseAch";

// Roles/groups are already loaded, so the picker filters them locally. A
// search equal to a uuid returns that item so the saved value resolves a label.
const fetchLocal = async (items, search) => {
  const term = (search || "").toLowerCase();
  const data = items
    .filter(
      (item) =>
        item.uuid === search || (item.name || "").toLowerCase().includes(term)
    )
    .map((item) => ({ uuid: item.uuid, code: item.name }));
  return { status: 200, data };
};

const LOCAL_OPTION_PROPS = {
  getOptionValue: (option) => option.uuid,
  formatOptionLabel: (option) => option.code,
  renderOption: (option) => (
    <div className="searchable-code-select__option-code">{option.code}</div>
  ),
};

export default function PerCompanyConfig({
  companyUuid,
  companyName,
  companyCode,
  config,
  availableRoles,
  onChange,
  onEmployeeSelect,
  errors,
  disabled,
  canManageAch,
}) {
  const displayName = companyCode
    ? `${companyName} (${companyCode})`
    : companyName;
  const roleError = errors[`role_${companyUuid}`];
  const groupsError = errors[`groups_${companyUuid}`];

  // ACH section renders only when both hold (plan §8 behavior 1): the
  // selected role is expense-enabled, and the logged-in admin holds
  // manage_ach for this company. Without either, the vault would reject the
  // calls anyway — this just avoids showing a section that can't be used.
  const selectedRole = availableRoles.find((r) => r.uuid === config.roleUuid);
  const achVisible = selectedRole?.expenses === true && canManageAch;
  const cardsVisible = selectedRole?.expenses === true;

  const availableGroups = config.availableGroups || [];

  const handleGroupSelect = (index, uuid) => {
    const group = availableGroups.find((g) => g.uuid === uuid);
    if (!group) return;
    const updated = config.groups.map((g, i) =>
      i === index ? { uuid: group.uuid, name: group.name } : g
    );
    onChange("groups", updated);
  };

  const handleGroupRemove = (index) => {
    const updated = config.groups.filter((_, i) => i !== index);
    onChange("groups", updated);
  };

  const handleGroupAdd = () => {
    const usedUuids = config.groups.map((g) => g.uuid);
    const first = availableGroups.find((g) => !usedUuids.includes(g.uuid));
    if (!first) return;
    onChange("groups", [
      ...config.groups,
      { uuid: first.uuid, name: first.name },
    ]);
  };

  const cards = config.cards || [];

  const handleCardChange = (index, field, value) => {
    const updated = cards.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    onChange("cards", updated);
  };

  const handleCardAdd = () => {
    onChange("cards", [
      ...cards,
      { uuid: null, description: "", glAccountCode: "" },
    ]);
  };

  const handleCardRemove = (index) => {
    onChange(
      "cards",
      cards.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="user-form__company-card">
      <div className="user-form__company-card-header">
        <span className="user-form__company-card-name">{displayName}</span>
        <label className="user-form__active-toggle">
          <input
            type="checkbox"
            checked={config.enabled !== false}
            onChange={(e) => onChange("enabled", e.target.checked)}
            disabled={disabled}
          />
          <span>Active</span>
        </label>
      </div>

      <div className="user-form__company-card-body">
        {/* Employee ID */}
        <div className="user-form__field">
          <SearchableEmployeeSelect
            label="Employee ID"
            value={config.employee || ""}
            onChange={(value) => onChange("employee", value)}
            onSelect={onEmployeeSelect}
            placeholder="Search for employee..."
            disabled={disabled}
          />
        </div>

        {/* Role */}
        <div className="user-form__field">
          <label className="user-form__label">
            Role <span className="danger">*</span>
          </label>
          {availableRoles.length === 0 ? (
            <input value="" placeholder="No roles available" disabled />
          ) : (
            <SearchableCodeSelect
              value={config.roleUuid || ""}
              companyUuid={companyUuid}
              onChange={(uuid) => onChange("roleUuid", uuid)}
              fetchOptions={(search) => fetchLocal(availableRoles, search)}
              {...LOCAL_OPTION_PROPS}
              placeholder="Search for role..."
              emptyMessage="No roles found"
              disabled={disabled}
            />
          )}
          {roleError && <span className="user-form__error">{roleError}</span>}
        </div>

        {/* Access Groups */}
        <div className="user-form__field">
          <label className="user-form__label">
            Access Groups <span className="danger">*</span>
          </label>
          {config.groupsLoading ? (
            <div className="user-form__groups-loading">Loading groups...</div>
          ) : availableGroups.length === 0 ? (
            <div className="user-form__groups-empty">
              No access groups configured
            </div>
          ) : (
            <>
              {config.groups.map((group, index) => {
                const otherSelectedUuids = config.groups
                  .filter((_, i) => i !== index)
                  .map((g) => g.uuid);
                const options = availableGroups.filter(
                  (ag) => !otherSelectedUuids.includes(ag.uuid)
                );
                return (
                  <div key={index} className="user-form__group-row">
                    <SearchableCodeSelect
                      value={group.uuid}
                      companyUuid={companyUuid}
                      onChange={(uuid) =>
                        uuid
                          ? handleGroupSelect(index, uuid)
                          : handleGroupRemove(index)
                      }
                      fetchOptions={(search) => fetchLocal(options, search)}
                      {...LOCAL_OPTION_PROPS}
                      placeholder="Search for access group..."
                      emptyMessage="No access groups found"
                      disabled={disabled}
                    />
                    <button
                      type="button"
                      onClick={() => handleGroupRemove(index)}
                      className="user-form__group-remove"
                      disabled={disabled}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {config.groups.length < availableGroups.length && (
                <button
                  type="button"
                  className="user-form__group-add"
                  onClick={handleGroupAdd}
                  disabled={disabled}
                >
                  + Add another access group
                </button>
              )}
            </>
          )}
          {groupsError && (
            <span className="user-form__error">{groupsError}</span>
          )}
        </div>

        {/* Expense Cards */}
        {cardsVisible && (
          <div className="user-form__field">
            <label className="user-form__label">Cards</label>
            {cards.length > 0 && (
              <div className="user-form__card-column-labels">
                <span>Description</span>
                <span>GL Account</span>
              </div>
            )}
            {cards.map((card, index) => (
              <div key={index} className="user-form__card-row">
                <input
                  type="text"
                  value={card.description}
                  onChange={(e) =>
                    handleCardChange(index, "description", e.target.value)
                  }
                  placeholder="Card description"
                  className="user-form__input"
                  disabled={disabled}
                />
                <SearchableGlAccountSelect
                  value={card.glAccountCode}
                  companyUuid={companyUuid}
                  onChange={(code) =>
                    handleCardChange(index, "glAccountCode", code)
                  }
                  placeholder="Search for GL account..."
                  disabled={disabled}
                />
                <button
                  type="button"
                  onClick={() => handleCardRemove(index)}
                  className="user-form__group-remove"
                  disabled={disabled}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="user-form__group-add"
              onClick={handleCardAdd}
              disabled={disabled}
            >
              + Add another card
            </button>
          </div>
        )}

        {achVisible && (
          <ExpenseAch
            ach={config.ach}
            achLoading={config.achLoading}
            onChange={(field, value) =>
              onChange("ach", { ...config.ach, [field]: value })
            }
            disabled={disabled}
            routingError={errors[`ach_routing_${companyUuid}`]}
            accountError={errors[`ach_account_${companyUuid}`]}
          />
        )}
      </div>
    </div>
  );
}
